import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { DEVELOP_BANNERS } from '@app/shared/constants/cognition';
import { Button, Heading, Paragraph, ScrollArea } from '@app/shared/ui/atoms/Common';
import { AUDIO_LIST } from '@app/shared/constants/media';
import { AudioPlayer } from '@app/shared/ui/organisms/Multimedia';
import { GlassModal, NotFound } from '@app/shared/ui/molecules/Common';
import { MediaData } from '@app/shared/types/player';
import { AudioSection } from '@app/shared/ui/organisms/Development';
import { useGetSettlementsQuery } from '@app/store/slices/settlements/settlementsApi';
import { SettlementImage } from '@app/shared/ui/molecules/Home';
import { Modal } from '@app/shared/ui/organisms/Common';
import { usePurchaseSettlement } from '@app/shared/hooks/usePurchaseSettlement';
import { Settlement } from '@app/store/slices/settlements/settlementsTypes';

interface BuySettlementModalState {
  isOpen: boolean;
  settlement: Settlement | null;
}

export const DevelopmentPage = () => {
  const { t } = useTranslation();
  const { id: sphereId = '0' } = useParams();
  const { value: isPlayerOpen, setValue: setIsPlayerOpen, setTrue: openPlayer } = useBoolean(false);

  const [buySettlementModal, setBuySettlementModal] = useState<BuySettlementModalState>({
    isOpen: false,
    settlement: null,
  });

  const [currentMedia, setCurrentMedia] = useState<MediaData>();
  const { purchaseSettlement, isPurchasingSettlement } = usePurchaseSettlement();
  const { data: settlements } = useGetSettlementsQuery();

  const sphere = DEVELOP_BANNERS.find((item) => item.number === Number(sphereId));

  const audios = useMemo(() => {
    return AUDIO_LIST.reduce(
      (acc, item) => {
        if (item.sphere === sphere?.title && item.type) {
          const settlement = settlements?.find((s) => s.id === item.settlementId);
          const isLocked = settlement ? !settlement.isPurchased : false;

          acc[item.type]?.push({ ...item, isLocked });
        }
        return acc;
      },
      {
        meditation: [] as MediaData[],
        insight: [] as MediaData[],
        practice: [] as MediaData[],
      }
    );
  }, [sphere, settlements]);

  const handleAudioClick = (media: MediaData) => {
    if (media.settlementId === undefined || media.settlementId === null) {
      setCurrentMedia(media);
      openPlayer();
      return;
    }

    const settlement = settlements?.find((s) => s.id === media.settlementId);

    if (!settlement || settlement.isPurchased) {
      setCurrentMedia(media);
      openPlayer();
      return;
    }

    setBuySettlementModal({
      isOpen: true,
      settlement,
    });
  };

  const sections = [
    { key: 'meditations', title: t('development.meditations'), list: audios.meditation },
    { key: 'insights', title: t('development.insights'), list: audios.insight },
    { key: 'practices', title: t('development.practices'), list: audios.practice },
  ];

  const isEmpty = !audios.meditation.length && !audios.insight.length && !audios.practice.length;

  return (
    <ScrollArea className="space-y-6">
      <Heading level={2} className="mb-2 mt-0 text-center text-xl">
        {t(`cognition.develop.banners.${sphere?.title}`)}
      </Heading>

      {isEmpty && <NotFound message={t('development.not_found')} />}

      {sections.map((section) => (
        <AudioSection
          key={section.key}
          title={section.title}
          list={section.list}
          onClick={handleAudioClick}
        />
      ))}

      <GlassModal
        isOpen={isPlayerOpen && !!currentMedia}
        onOpenChange={() => setIsPlayerOpen((prevState) => !prevState)}
      >
        {currentMedia && <AudioPlayer tracks={[currentMedia]} />}
      </GlassModal>

      {buySettlementModal.settlement && (
        <Modal
          isOpen={buySettlementModal.isOpen}
          onOpenChange={() => setBuySettlementModal({ isOpen: false, settlement: null })}
          featuredImg={
            <SettlementImage
              imageUrl={buySettlementModal.settlement?.imageUrl ?? ''}
              name={buySettlementModal.settlement.name ?? ''}
              className="grayscale static size-[150px]"
            />
          }
          contentClassName="bg-background shadow-blue"
        >
          <Paragraph>
            {t('development.buy_modal', { title: buySettlementModal.settlement.name })}
          </Paragraph>

          <Button
            onClick={() => purchaseSettlement(buySettlementModal.settlement)}
            disabled={isPurchasingSettlement}
          >
            {isPurchasingSettlement ? t('common.wait') : t('common.buy')}
          </Button>
        </Modal>
      )}
    </ScrollArea>
  );
};
